'use client';

import { useEffect, useState } from 'react';
import { useCommerceSource, type CommerceSource } from '@akademi/site-builder-runtime';

/**
 * The trade saving the shop's own prices give, as the backend computes it
 * (`TradeSavingsDto`, contract `commerce/docs/TRADE_AND_INSTALLER_JOBS_CONTRACT.md` §1).
 *
 * <p>It is never typed in: the backend compares the RETAIL and TRADE prices of the live
 * catalogue, and sends `savings: null` when the shop has not switched it on or has too few
 * products to say. The page then shows no number at all.
 */
export interface TradeSavings {
  /** Median saving, whole percent, rounded down. */
  typical: number;
  /** 90th-percentile saving, whole percent, rounded down. */
  upTo: number;
}

/** A host commerce source that can read the trade application requirements (the storefront's does). */
type RequirementsSource = CommerceSource & { tradeApplicationRequirements?: () => Promise<unknown> };

const percent = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 && value < 100 ? Math.floor(value) : null;

/** The saving from a requirements answer, or null when there is none worth showing. */
export function readTradeSavings(requirements: unknown): TradeSavings | null {
  const savings = (requirements as { savings?: { typicalPct?: unknown; upToPct?: unknown } | null } | null)?.savings;
  if (!savings || typeof savings !== 'object') return null;
  const typical = percent(savings.typicalPct);
  if (typical === null) return null;
  const upTo = percent(savings.upToPct);
  return { typical, upTo: upTo !== null && upTo > typical ? upTo : typical };
}

/** The sentence for a saving, through the theme's translator. */
export function tradeSavingsText(
  savings: TradeSavings,
  t: (key: string, values?: Record<string, string | number>) => string,
): string {
  return savings.upTo > savings.typical
    ? t('trade.savings', { typical: savings.typical, upTo: savings.upTo })
    : t('trade.savingsFlat', { typical: savings.typical });
}

/**
 * Reads the saving after mount, through the host's commerce source
 * (`GET /api/storefront/trade-applications/requirements`). Null on the server, while
 * loading, on a host without the call, and whenever the call fails: a failure hides the
 * line, it never breaks the band.
 */
export function useTradeSavings(): TradeSavings | null {
  const commerce = useCommerceSource() as RequirementsSource | null;
  const [savings, setSavings] = useState<TradeSavings | null>(null);
  useEffect(() => {
    const read = commerce?.tradeApplicationRequirements;
    if (!read) return;
    let cancelled = false;
    Promise.resolve()
      .then(() => read.call(commerce))
      .then((answer) => {
        if (!cancelled) setSavings(readTradeSavings(answer));
      })
      .catch(() => {
        if (!cancelled) setSavings(null);
      });
    return () => {
      cancelled = true;
    };
  }, [commerce]);
  return savings;
}
